import { BreadcrumbItem, Breadcrumbs, Chip, Link } from '@sortlist-frontend/design-system';
import { ArrowRight } from '@sortlist-frontend/design-system/icons';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatCustomDate } from '@sortlist-frontend/utils';
import { Fragment } from 'react';

import { rankingConfig } from '_features/RankingPage/ranking.config';

import type { GuidePageContent } from '../types';

interface GuideHeaderProps {
  content: Pick<GuidePageContent, 'breadcrumbs' | 'header' | 'author' | 'updatedAt' | 'locale'>;
}

export const GuideHeader = (props: GuideHeaderProps) => {
  const { content } = props;
  const { breadcrumbs, header, author, updatedAt, locale } = content;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);
  const lastBreadcrumbIndex = breadcrumbs.length - 1;
  const flow = header.flow;

  return (
    <header className="border-b border-secondary-200 pb-10 pt-6 md:pb-14">
      <Breadcrumbs className="mb-8">
        {breadcrumbs.map((crumb, index) => (
          <BreadcrumbItem key={crumb.href + crumb.label} href={index === lastBreadcrumbIndex ? undefined : crumb.href}>
            {crumb.label}
          </BreadcrumbItem>
        ))}
      </Breadcrumbs>
      <ul className="mb-3 flex flex-wrap gap-2">
        {header.chips.map((chip) => (
          <li key={chip}>
            <Chip color="primary" variant="flat" radius="full" size="sm">
              {chip}
            </Chip>
          </li>
        ))}
      </ul>
      <h1 className="max-w-4xl break-words text-3xl font-semibold leading-tight tracking-tight text-secondary-950 md:text-5xl">
        {header.title}
      </h1>
      <p className="mt-5 flex flex-wrap items-center gap-x-2 text-sm text-secondary-500">
        <span>{t('longtail:ranking.author.by')}</span>
        <Link href="#author" color="foreground" underline="always" className="text-sm font-semibold">
          {author.name}
        </Link>
        <span>
          ·{' '}
          {t('longtail:ranking.methodology.updatedOn', {
            date: formatCustomDate(updatedAt, { dateStyle: 'long', timeZone: 'UTC' }, locale),
          })}
        </span>
        {header.sourcesLabel != null ? (
          <Fragment>
            <span>·</span>
            <Link href="#methodology" color="primary" underline="always" className="text-sm">
              {header.sourcesLabel}
            </Link>
          </Fragment>
        ) : null}
      </p>
      {flow != null ? (
        <ol aria-label={flow.label} className="mt-8 flex flex-wrap items-center gap-2">
          {flow.steps.map((step, index) => (
            <li key={step} className="flex items-center gap-2">
              <Chip color="secondary" variant="flat" radius="full" size="md">
                {step}
              </Chip>
              {index < flow.steps.length - 1 ? (
                <ArrowRight size={16} className="text-secondary-400" aria-hidden="true" />
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </header>
  );
};
